import { createStyles, WithStyles } from '@material-ui/core'
import List from '@material-ui/core/List/List'
import ListItem from '@material-ui/core/ListItem/ListItem'
import ListItemText from '@material-ui/core/ListItemText/ListItemText'
import withStyles from '@material-ui/core/styles/withStyles'
import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import { FixedSizeList, ListChildComponentProps } from 'react-window'
import action from 'store/actions'
import {
  getDraft,
  getProjects,
  getProjectsFetchingStatus,
  getSourceProjects,
} from 'store/selectors'
import withSelectedValues, { DataloaderInfo } from 'utils/withSelectedValues'
import CenteredSpinner from '../../../shared/CenteredSpinner'
import SelectorInput from './selectorMenu/SelectorInput'

const styles = createStyles({
  link: {
    textDecoration: 'none',
    color: 'black',
  },
  draftStatus: {
    position: 'absolute',
    right: 0,
    bottom: 0,
    fontSize: '11px',
  },
  list: {
    width: '100%',
    overflow: 'visible',
    height: 'auto',
  },
})

interface StateProps {
  projects: ReduxStore.Projects.Data.Project[]
  projectsStatus: boolean
  draft: ReduxStore.Draft.State
}

interface DispatchProps {
  fetchProjects: () => void
  fetchDraft: (cpId: number) => void
  toggleAndInitDraftMode: (isEnabled: boolean, projectId: number) => void
}

type Props = StateProps & DispatchProps & WithStyles<typeof styles> & DataloaderInfo

const ProjectSelector: React.FC<Props> = props => {
  const [searchText, setSearchText] = useState('')
  const [dynamicSearch, setDynamicSearch] = useState(false)

  useEffect(() => {
    const { fetchProjects } = props
    fetchProjects()
  }, [])

  const getSearchDisplayText = useMemo(() => {
    const {
      selectedProject,
      projects,
      draft: { isEnabled, draftData },
    } = props
    if (dynamicSearch) {
      return searchText
    }

    const displayProject =
      projects && selectedProject && projects.find(pro => pro.id === +selectedProject)

    const project =
      displayProject &&
      (displayProject.isDraft
        ? projects.find(pro => pro.projectId === displayProject.sourceProjectId)
        : displayProject)
    return project ? project.projectName : ''
  }, [dynamicSearch, searchText, props.selectedProject, props.projects, props.draft])

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
    setDynamicSearch(true)
  }, [])

  const filterProjects = useCallback((projects: ReduxStore.Projects.Data.Project[]) => {
    const lowerSearchText = searchText.toLowerCase()
    return projects.filter(
      ({ projectName, companyName }) =>
        (projectName && projectName.toLowerCase().includes(lowerSearchText)) ||
        (companyName && companyName.toLowerCase().includes(lowerSearchText))
    )
  }, [searchText])

  // Memoize the row renderer for virtualized list
  const handleProjectClick = useCallback((project: ReduxStore.Projects.Data.Project) => {
    const { fetchDraft, toggleAndInitDraftMode } = props;
    const { projectName, projectId, companyProjectId } = project;
    
    setSearchText(projectName)
    setDynamicSearch(false)
    if (projectId && companyProjectId) {
      fetchDraft(companyProjectId)
      toggleAndInitDraftMode(false, projectId)
    }
    
    // Analytics tracking
    try {
      // @ts-ignore
      if (window.analytics) {
        // @ts-ignore
        window.analytics.track('Project Selected', {
          project: {
            id: projectId,
            name: projectName,
          },
          platform: 'dataloader',
        })
      }
    } catch (error) {
      console.error('Analytics error:', error);
    }
  }, [props.fetchDraft, props.toggleAndInitDraftMode]);
  
  // Render row function for virtualized list
  const renderRow = useCallback(({ index, style }: ListChildComponentProps) => {
    const { selectedProject, classes } = props;
    
    // Get the projects list using a scope-local function to avoid stale closures
    const getFilteredProjects = () => {
      const { projects } = props;
      if (!projects) return [];
      
      const sourceProjects = projects.filter(
        (project: ReduxStore.Projects.Data.Project) => !project.isDraft
      );
      
      return dynamicSearch ? filterProjects(sourceProjects) : sourceProjects;
    };
    
    const filteredProjects = getFilteredProjects();
    if (index >= filteredProjects.length) return null;
    
    const project = filteredProjects[index];
    const { id, projectName, companyName, projectId } = project;
    
    return (
      <div style={style}>
        <Link
          to={`/${id}/${projectId}`}
          className={classes.link}
          key={`${id}-${projectName}-${companyName}`}
          onClick={() => handleProjectClick(project)}
        >
          <ListItem
            button={true}
            selected={!!selectedProject && id === selectedProject}
            onClick={() => handleProjectClick(project)}
          >
            <ListItemText primary={projectName} secondary={companyName} />
          </ListItem>
        </Link>
      </div>
    );
  }, [props, dynamicSearch, filterProjects, handleProjectClick]);

  const projectsList = useMemo(() => {
    const { projectsStatus, projects, classes } = props;

    if (projectsStatus === true) {
      return <CenteredSpinner />
    }

    if (!projects) {
      return null
    }

    if (projects.length === 0) {
      return 'No projects to display'
    }

    const sourceProjects = projects.filter(
      (project: ReduxStore.Projects.Data.Project) => !project.isDraft
    )

    const filteredProjects = dynamicSearch ? filterProjects(sourceProjects) : sourceProjects
    if (filteredProjects.length === 0) {
      return 'No projects match your search'
    }
    
    // Default item height and calculate height based on available space
    const itemHeight = 80; // Slightly larger than DrawingSelector to account for secondary text
    
    // Always use virtualization and take full height of the available space
    const useVirtualization = true;
    
    // Set list to take full available height
    const viewportHeight = window.innerHeight - 80; // Subtract some space for headers
    
    // Create a stable key for the FixedSizeList to prevent unnecessary recreation
    const listKey = `project-list-${filteredProjects.length}`;
    
    return useVirtualization ? (
      <FixedSizeList
        key={listKey}
        className={classes.list}
        height={viewportHeight}
        width="100%"
        itemCount={filteredProjects.length}
        itemSize={itemHeight}
        overscanCount={5}
        style={{ flex: 1 }}
      >
        {renderRow}
      </FixedSizeList>
    ) : (
      <List>
        {filteredProjects.map((project) => {
          const { id, projectName, companyName, projectId } = project;
          return (
            <Link
              to={`/${id}/${projectId}`}
              className={classes.link}
              key={`${id}-${projectName}-${companyName}`}
              onClick={() => handleProjectClick(project)}
            >
              <ListItem
                button={true}
                selected={!!props.selectedProject && id === props.selectedProject}
                onClick={() => handleProjectClick(project)}
              >
                <ListItemText primary={projectName} secondary={companyName} />
              </ListItem>
            </Link>
          );
        })}
      </List>
    )
  }, [props, dynamicSearch, filterProjects, renderRow])

  return (
    <SelectorInput label="Project" onChange={handleChange} value={getSearchDisplayText}>
      {projectsList}
    </SelectorInput>
  )
}

const mapStateToProps = (state: ReduxStore.State) => ({
  projects: getProjects(state),
  projectsStatus: getProjectsFetchingStatus(state),
  draft: getDraft(state),
})

const mapDispatchToProps = {
  fetchProjects: action.fetchProjects,
  fetchDraft: action.fetchDraft,
  toggleAndInitDraftMode: action.toggleAndInitDraftMode,
}

export default withStyles(styles)(
  withSelectedValues(
    // @ts-ignore
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(ProjectSelector)
  )
)
